import { auditLogsQuery } from "@common/api/audit"
import { PaginationControls } from "@common/components/shared/pagination-controls"
import { Skeleton } from "@common/components/ui/skeleton"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { ScrollText } from "lucide-react"
import { Suspense } from "react"
import z from "zod"
import { ListEmpty } from "@/components/layout/list-empty"
import {
  calculateSkip,
  calculateTotalPages,
  getPageNumbers,
} from "@/lib/pagination"
import { requireSystemUser } from "@/lib/require-system-user"
import { AuditLogTable } from "./-components/audit-log-table"

const ITEMS_PER_PAGE = 20

const filterSchema = z.object({
  page: z.number().optional().catch(1),
})

function listParams(page: number) {
  return { skip: calculateSkip(page, ITEMS_PER_PAGE), limit: ITEMS_PER_PAGE }
}

export const Route = createFileRoute("/_layout/settings/system_/audit-log/")({
  staticData: {
    breadcrumb: [
      { label: "System Settings", href: "/settings/system" },
      { label: "Audit log" },
    ],
  },
  validateSearch: (search) => filterSchema.parse(search),
  beforeLoad: ({ context: { queryClient } }) =>
    requireSystemUser(queryClient, "/settings/system"),
  loaderDeps: ({ search }) => ({ page: search.page ?? 1 }),
  loader: ({ context: { queryClient }, deps }) =>
    queryClient.ensureQueryData(auditLogsQuery(listParams(deps.page))),
  component: AuditLogPage,
})

function AuditLogPage(): React.ReactElement {
  const page = Route.useSearch().page ?? 1

  return (
    <Suspense fallback={<AuditLogSkeleton />}>
      <AuditLogResults page={page} />
    </Suspense>
  )
}

function AuditLogResults({ page }: { page: number }): React.ReactElement {
  const navigate = useNavigate({ from: Route.fullPath })
  const { data } = useSuspenseQuery(auditLogsQuery(listParams(page)))

  if (data.data.length === 0) {
    return (
      <ListEmpty
        icon={ScrollText}
        title="No recorded changes"
        body="Changes to this tenant's data will appear here as they happen."
      />
    )
  }

  const totalPages = calculateTotalPages(data.count, ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      <AuditLogTable logs={data.data} />
      <div className="px-8">
        <PaginationControls
          currentPage={page}
          totalPages={totalPages}
          pageNumbers={getPageNumbers(page, totalPages)}
          onPageChange={(next) =>
            navigate({ search: (previous) => ({ ...previous, page: next }) })
          }
        />
      </div>
    </div>
  )
}

function AuditLogSkeleton(): React.ReactElement {
  return (
    <div className="space-y-4 px-8">
      {[1, 2, 3].map((index) => (
        <Skeleton key={index} className="h-12 w-full rounded-lg" />
      ))}
    </div>
  )
}
